import React, { createContext, useContext, useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { 
  getVisiblePaths, 
  useMapPointSelect,
  useModeSwitching
} from '../Services';
import { cleanupMtoSession } from '../Services/UtilityServices/SessionService';
import { usePureMemoryPathManagement } from '../Services/PathServices/PureMemoryPathService';

// Create context
const MtoContext = createContext();

// Provide a hook to use the context
export const useMto = () => {
  const context = useContext(MtoContext);
  if (!context) {
    throw new Error('useMto must be used within an MtoProvider');
  }
  return context;
};


// Context provider component
export const MtoProvider = ({ children }) => {
  // Base state
  const [locations, setLocations] = useState([]);
  const [startId, setStartId] = useState('');
  const [endId, setEndId] = useState('');
  const [showPanel, setShowPanel] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [maxCapacity, setMaxCapacity] = useState(0);

  // Demand state for capacity constraint calculation
  const [demand, setDemand] = useState(0);

  // Mode capacities for each transport mode
  const [modeCapacities, setModeCapacities] = useState({
    electric_motorcycle: 0,
    truck: 0,
    railway: 0,
    shipping: 0
  });

  // Solve result state (persistent across mode switches)
  const [autoSolveResult, setAutoSolveResult] = useState(null);
  
  // Custom path metadata (including capacity-adjusted duration)
  const [customPathMetadata, setCustomPathMetadata] = useState(null);

  // Connection state for connection mode
  const [connections, setConnections] = useState([]);

  // Use existing service hooks
  const { 
    currentMode, 
    setCurrentMode,
    currentModeRef,
    setupSubmode, 
    handleSubmodeSwitching
  } = useModeSwitching();

  // Use navigation management
  const isMountedRef = useRef(true);
  const isNavigatingRef = useRef(false);
  
  const goToHome = useCallback(() => {
    if (isMountedRef.current) {
      isNavigatingRef.current = true;
      window.location.href = '/';
    }
  }, []);

  // Use pure memory path management hook (like TSP)
  const {
    autoPath,
    setAutoPath,
    customPath,
    setCustomPath,
    autoVehicleCount,
    setAutoVehicleCount,
    customVehicleCount,
    setCustomVehicleCount,
    prevAutoPathRef,
    prevCustomPathRef,
    setPath,
    saveAutoPath,
    saveCustomPath,
    clearAllPaths: clearMemoryPaths,
    clearAutoPath,
    clearCustomPath
  } = usePureMemoryPathManagement(locations);

  // Map point selection hook
  const { 
    mapPointSelectCallback, 
    setMapPointSelect,
    clearMapPointSelect 
  } = useMapPointSelect();

  // Map reference for location jumping
  const mapRef = useRef(null);

  // Function to jump to a specific location on the map
  const jumpToLocation = useCallback((latitude, longitude, zoom = 10) => {
    if (!mapRef.current || !mapRef.current.loaded()) {
      console.warn('Map not ready yet');
      return;
    }

    try {
      mapRef.current.flyTo({
        center: [longitude, latitude],
        zoom: zoom,
        duration: 1000, // Animation duration in milliseconds
        essential: true // This animation is considered essential with respect to prefers-reduced-motion
      });
    } catch (error) {
      console.error('Failed to jump to location:', error);
    }
  }, []);

  // Function to set map reference (to be called from MapComponent)
  const setMapRef = useCallback((map) => {
    mapRef.current = map;
  }, []);

  // Callback to clear all paths (pure memory - like TSP)
  const clearAllPaths = useCallback(async () => {
    try {
      // Clear paths from memory only
      clearMemoryPaths();
      
      // Optional: clean up any backend storage
      await cleanupMtoSession();
      
      console.log('Cleared all paths from memory');
    } catch (err) {
      console.error('Error clearing paths:', err);
    }
  }, [clearMemoryPaths]);

  // Initialize session data - Pure memory approach (like TSP)
  const initializeSession = useCallback(async () => {
    try {
      // Clean up any existing session data
      await cleanupMtoSession();
      
      // Initialize with empty state - pure memory approach
      setIsLoading(true);
      setAutoPath([]);
      setCustomPath([]);
    } catch (error) {
      console.error('Session initialization failed:', error);
      // If cleanup fails, still initialize with empty state
      setAutoPath([]);
      setCustomPath([]);
    } finally {
      setIsLoading(false);
    }
  }, [setAutoPath, setCustomPath]);

  // Calculate visible paths for current mode
  const visiblePaths = useMemo(() => {
    const paths = getVisiblePaths(currentMode, autoPath, customPath);

    // Add vehicle count info for comparison mode
    if (currentMode === 'Comparison Mode') {
      paths.customVehicleCount = customVehicleCount;
      paths.autoVehicleCount = autoVehicleCount;
    }
    
    return paths;
  }, [currentMode, autoPath, customPath, customVehicleCount, autoVehicleCount]);

  // Initialize component on mount
  const initialize = useCallback(() => {
    isMountedRef.current = true;
    isNavigatingRef.current = false;
    initializeSession();
    
    return () => {
      isMountedRef.current = false;
    };
  }, [initializeSession]);

  // Value provided to context
  const contextValue = {
    // Base state
    locations,
    setLocations,
    startId,
    setStartId,
    endId,
    setEndId,
    showPanel,
    setShowPanel,
    isLoading,
    maxCapacity,
    setMaxCapacity,
    
    // Demand for capacity constraint
    demand,
    setDemand,
    
    // Mode capacities
    modeCapacities,
    setModeCapacities,
    
    // Auto solve result
    autoSolveResult,
    setAutoSolveResult,
    
    // Custom path metadata
    customPathMetadata,
    setCustomPathMetadata,
    
    // Navigation
    goToHome,
    isMountedRef,
    isNavigatingRef,
    initialize,
    
    // Mode management
    currentMode,
    setCurrentMode,
    currentModeRef,
    setupSubmode,
    handleSubmodeSwitching,
    
    // Path management
    autoPath,
    setAutoPath,
    customPath,
    setCustomPath,
    prevAutoPathRef,
    prevCustomPathRef,
    setPath: (path, metadata) => setPath(path, currentMode, metadata),
    saveAutoPath,
    saveCustomPath,
    clearAllPaths,
    visiblePaths,
    
    // Vehicle count management
    customVehicleCount,
    setCustomVehicleCount,
    autoVehicleCount,
    setAutoVehicleCount,
    
    // Map interaction
    mapPointSelectCallback,
    setMapPointSelect,
    clearMapPointSelect,
    jumpToLocation,
    setMapRef,
    mapRef,
    
    // Connection management
    connections,
    setConnections
  };

  return (
    <MtoContext.Provider value={contextValue}>
      {children}
    </MtoContext.Provider>
  );
}; 